import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  ConditionalRender,
} from '@crossnokaye/ux-common';

export interface SidebarAccordionItem<T> {
  value: string;
  title: string;
  data: T;
}

export interface SidebarAccordionState<T> {
  items: SidebarAccordionItem<T>[];
  expanded: string[];
  setExpanded: (expanded: string[]) => void;
}

interface SidebarAccordionTemplateProps<T> {
  state: SidebarAccordionState<T>;
  variant?: 'small' | 'medium';
  keyGenerator: (item: SidebarAccordionItem<T>) => string;
  contentComponent: React.ComponentType<{ item: SidebarAccordionItem<T> }>;
  emptyComponent?: React.ComponentType;
  /** Override AccordionContent className (default: "pb-0") */
  contentClassName?: string;
}

export const SidebarAccordionTemplate = <T,>({
  state,
  variant = 'medium',
  keyGenerator,
  contentComponent: Content,
  emptyComponent: EmptyComponent,
  contentClassName = 'pb-0',
}: SidebarAccordionTemplateProps<T>) => {
  const { items, expanded, setExpanded } = state;

  return (
    <Accordion type={'multiple'} value={expanded} onValueChange={setExpanded}>
      {items.map((item: SidebarAccordionItem<T>) => (
        <AccordionItem key={keyGenerator(item)} className={'border-0 mt-thin'} value={item.value}>
          <AccordionTrigger
            variant={variant}
            title={item.title}
            className="font-semibold text-xs bg-transparent pr-thin pl-gutter-sm"
          >
            {item.title}
          </AccordionTrigger>
          <AccordionContent className={contentClassName}>
            <Content item={item} />
          </AccordionContent>
        </AccordionItem>
      ))}
      <ConditionalRender condition={items.length === 0}>
        {EmptyComponent && <EmptyComponent />}
      </ConditionalRender>
    </Accordion>
  );
};
